import type { SchemaFlujoEntidadIn } from "./FormsFlujo/Flujo.schema";
import type { SchemaEntradaSubProcesoIn } from "./FormsFlujo/Entrada.schema";
import { MyFlow } from "@/components/ui/Flow_Diagram/FlujoBaseSVG";

export function DiagramaFlujo(props: {
	readonly entities: SchemaFlujoEntidadIn[];
	readonly erroring: string[];
	readonly selectedId: string | null;
	readonly setSelectedId: Setter<string | null>;
}) {
	const sortedNodes = sortNodes(props.entities);
	const nodes = parseNodes(sortedNodes, props.erroring);
	const edges = parseUnions(sortedNodes);

	return (
		<div style={{ padding: "10px", overflow: "auto", flex: 1 }}>
			<MyFlow
				nodes={nodes}
				edges={edges}
				selectedId={props.selectedId}
				onNodeClick={(id) => props.setSelectedId(id)}
				onPaneClick={() => props.setSelectedId(null)}
			/>
		</div>
	);
}

function parseNodes(nodes: SchemaFlujoEntidadIn[], erroring: string[]) {
	const nodeError = (nodeId: string) => erroring.includes(nodeId);
	const cutLeft = (node: SchemaFlujoEntidadIn) => node.tipo === "entrada" || node.tipo === "proceso";
	const cutRight = (node: SchemaFlujoEntidadIn) => node.tipo === "salida" || node.tipo === "proceso" || node.subtipo === "sub_proceso";
	const color = (node: SchemaFlujoEntidadIn) => {
		switch (node.tipo) {
			case "entrada": {
				switch (node.subtipo) {
					case "raw_co2": return "#2E86DE";
					case "materia_prima": return "#54A0FF";
					case "producto": return "#0ABDE3";
					case "sub_proceso": {
						if (node.externo == "propio") return "#FECA57";
						else return "#EE5253";
					}
					default: return "#607dab";
				}
			}
			case "proceso": {
				if (node.externo === "propio") return "#FF9F43";
				else return "#FF6B6B";
			}
			case "salida": {
				switch (node.subtipo) {
					case "raw_co2": return "#1DD1A1";
					case "residuo": return "#10AC84";
					case "producto": return "#55EFC4";
					case "main_producto": return "#51ffdc";
					default: return "#607dab";
				}
			}
		}
	};
	const label = (node: SchemaFlujoEntidadIn) => {
		const unidad = "unidad" in node ? node.unidad : "";
		if (unidad == "_u" && "cantidad" in node) {
			return `x${node.cantidad} ${node.nombre}`;
		}

		switch (node.tipo) {
			case "entrada": return `${node.nombre}\n${node.cantidad} ${unidad}`;
			case "proceso": return node.nombre;
			case "salida": return `${node.nombre}\n${node.cantidad} ${unidad}`;
		}
	};

	return nodes.map((node) => ({
		id: node.id,
		label: label(node),
		node: Node(color(node), nodeError(node.id), cutLeft(node), cutRight(node)),
		size: { width: 175, height: 75 },
	}));
}
function parseUnions(nodes: SchemaFlujoEntidadIn[]): Array<[string, string]> {
	const unions = new Array<[string, string]>();

	for (const node of nodes) {
		if (node.tipo === "entrada") {
			unions.push([node.id, node.idProcesoTo ?? node.idProyecto]);
		} else if (node.tipo === "salida") {
			unions.push([node.idProcesoFrom ?? node.idProyecto, node.id]);
		}
	}


	return unions;
}

/**
 * Sorts nodes in a way that the main process(with basic inputs and outputs) is first, then sub-processes(with basic inputs and outputs) in order of their depth.
 * This seems to prevent edges from crossing each other in the flow diagram.
 */
function sortNodes(nodes: SchemaFlujoEntidadIn[]): SchemaFlujoEntidadIn[] {
	const mainFlow = nodes.find((node) => node.tipo === "proceso");
	if (!mainFlow) return nodes;

	const subprocesoById = new Map<string, SchemaEntradaSubProcesoIn>();
	const entitiesByProcess = new Map<string, SchemaFlujoEntidadIn[]>();
	for (const node of nodes) {
		if (node.tipo === "proceso") continue;
		if (node.tipo === "entrada" && node.subtipo === "sub_proceso") {
			subprocesoById.set(node.id, node);
			continue;
		}

		const linksTo = node.tipo === "entrada" ? node.idProcesoTo : node.idProcesoFrom;

		const entities = entitiesByProcess.get(linksTo ?? node.idProyecto) ?? [];
		entities.push(node);
		entitiesByProcess.set(linksTo ?? node.idProyecto, entities);
	}

	const depthById = new Map<string, number>([[mainFlow.id, 0]]);

	function getDepth(id: string): number {
		const cached = depthById.get(id);
		if (cached !== undefined) return cached;

		const node = subprocesoById.get(id);
		if (!node) return 0;

		const depth = getDepth(node.idProcesoTo ?? node.idProyecto) + 1;
		depthById.set(id, depth);

		return depth;
	}

	// Diria que esta ultima parte se puede hacer con depthById directamente, sin mas arrays y sorts, pero bastante tiempo he perdido.

	const sortedSubprocesos = [...subprocesoById.values()].sort((a, b) => {
		const depthDiff = getDepth(a.id) - getDepth(b.id);

		if (depthDiff == 0) return a.id.localeCompare(b.id);
		else return depthDiff;
	});

	const result: SchemaFlujoEntidadIn[] = [];
	const mainFlowEntities = entitiesByProcess.get(mainFlow.id) ?? [];
	result.push(mainFlow, ...mainFlowEntities);

	for (const subproceso of sortedSubprocesos) {
		const subprocesoEntities = entitiesByProcess.get(subproceso.id) ?? [];
		result.push(subproceso, ...subprocesoEntities);
	}

	return result;
}

function Node(color: string, erroring: boolean, cutLeft: boolean, cutRight: boolean) {
	let path: string;
	let pathErr: string;
	if (cutLeft && cutRight) {
		path = "M20 0 L175 0 L155 75 L0 75 Z";
		pathErr = "M21 2 L173 2 L153.5 73 L2 73 Z";
	} else if (cutLeft) {
		path = "M0 0 L175 0 L155 75 L0 75 Z";
		pathErr = "M2 2 L173 1 L153.5 73 L2 73 Z";
	} else if (cutRight) {
		path = "M20 0 L175 0 L175 75 L0 75 Z";
		pathErr = "M21 2 L173 1 L173 73 L2 73 Z";
	} else {
		path = "M0 0 L175 0 L175 75 L0 75 Z";
		pathErr = "M2 2 L173 1 L173 73 L2 73 Z";
	}

	return (
		<svg version="1.1" viewBox="0 0 175 75" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
			<path d={path} fill={color} />
			{erroring && (
				<path d={pathErr} fill="none" stroke="red" strokeWidth="4" strokeDasharray="10 6" />
			)}
		</svg>
	);
}